import { computed } from "vue";
import { useDashboardTab } from "./useDashboardTab";
import { getEconomic } from "@/services/dashboard/dashboard";
import { objectToChartData, extractChartValue } from "@/utils/chartFormatters";
import { getSeasonName } from "@/composables/admin/dashboard/seasonUtils";

/**
 * Configuración de los gráficos del panel económico.
 * @type {Array<{ field: string, type: string, group: string, labelKey: string, descriptionKey: string }>}
 */
const CHART_CONFIG = [
    {
        field: "metodos_pago",
        type: "PieChart",
        group: "abonos",
        labelKey: "back.dashboard.economic.charts.metodos_pago.title",
        descriptionKey: "back.dashboard.economic.charts.metodos_pago.description",
    },
    {
        field: "canales_venta",
        type: "PieChart",
        group: "abonos",
        labelKey: "back.dashboard.economic.charts.canales_venta.title",
        descriptionKey: "back.dashboard.economic.charts.canales_venta.description",
    },
    {
        field: "renovados_nuevos_no_renovados",
        type: "PieChart",
        group: "abonos",
        labelKey:
            "back.dashboard.economic.charts.renovados_nuevos_no_renovados.title",
        descriptionKey:
            "back.dashboard.economic.charts.renovados_nuevos_no_renovados.description",
    },
    {
        field: "compradores_rango_edad",
        type: "ColumnChart",
        group: "entradas",
        labelKey: "back.dashboard.economic.charts.rango_edad.title",
        descriptionKey: "back.dashboard.economic.charts.rango_edad.description",
    },
    {
        field: "compra_por_dispositivo",
        type: "PieChart",
        group: "entradas",
        labelKey: "back.dashboard.economic.charts.compra_por_dispositivo.title",
        descriptionKey:
            "back.dashboard.economic.charts.compra_por_dispositivo.description",
    },
    {
        field: "pedidos_por_tamano",
        type: "PieChart",
        group: "entradas",
        labelKey: "back.dashboard.economic.charts.pedidos_por_tamano.title",
        descriptionKey:
            "back.dashboard.economic.charts.pedidos_por_tamano.description",
    },
    {
        field: "compradores_recurrentes_nuevos",
        type: "PieChart",
        group: "entradas",
        labelKey:
            "back.dashboard.economic.charts.compradores_recurrentes_nuevos.title",
        descriptionKey:
            "back.dashboard.economic.charts.compradores_recurrentes_nuevos.description",
    },
];

/**
 * Composable del panel económico del dashboard.
 * Gestiona la carga, normalización y cómputo de KPIs económicos
 * (abonos y entradas) con soporte multitemporada.
 *
 * Delega el fetching, cancelación y retardo mínimo a useDashboardTab.
 *
 * @param {import('vue').Ref<number[]>} seasonIdsRef — ref con los IDs de temporada seleccionados
 * @param {import('vue').Ref<Array>} seasonsRef — ref con la lista de temporadas (para obtener nombres)
 * @returns {{
 *     economicData: import('vue').Ref<object|null>,
 *     loading: import('vue').Ref<boolean>,
 *     error: import('vue').Ref<string|null>,
 *     kpis: import('vue').ComputedRef<{ abonos: object, entradas: object }|null>,
 *     chartData: import('vue').ComputedRef<Map<string, Array>>,
 *     chartConfigs: Array,
 *     refresh: () => Promise<void>,
 *     isMultiSeason: import('vue').ComputedRef<boolean>,
 *     geographyTableData: import('vue').ComputedRef<{ provincias: Array, localidades: Array, codigos_postales: Array }>,
 *     seasonBreakdown: import('vue').ComputedRef<{ abonos: Array, entradas: Array, totals: object }|null>,
 * }}
 */
export function useEconomic(seasonIdsRef, seasonsRef) {
    const {
        data: economicData,
        loading,
        error,
        isMultiSeason,
        currentSeasonEntry,
        refresh,
    } = useDashboardTab(seasonIdsRef, seasonsRef, getEconomic);

    // ==================== Computed ====================

    /**
     * KPIs agregados cuando hay múltiples temporadas.
     * Suma métricas aditivas y calcula media ponderada para precio medio.
     */
    const aggregateKpis = computed(() => {
        if (!isMultiSeason.value || !economicData.value) return null;
        const perSeason = economicData.value?.data?.per_season || {};
        const entries = Object.values(perSeason).filter(Boolean);
        if (entries.length === 0) return null;

        const totalAbonosIngresos = entries.reduce(
            (sum, e) => sum + (e.abonos?.ingresos_temporada || 0),
            0,
        );
        const totalAbonados = entries.reduce(
            (sum, e) => sum + (e.abonos?.numero_abonados_temporada || 0),
            0,
        );
        const totalAbonosPrecioPonderado = entries.reduce((sum, e) => {
            const count = e.abonos?.numero_abonados_temporada || 0;
            return sum + (e.abonos?.precio_medio_temporada || 0) * count;
        }, 0);

        const totalEntradasVendidas = entries.reduce(
            (sum, e) => sum + (e.entradas?.vendidas_temporada || 0),
            0,
        );
        const totalEntradasPrecioPonderado = entries.reduce((sum, e) => {
            const count = e.entradas?.vendidas_temporada || 0;
            return sum + (e.entradas?.precio_medio_temporada || 0) * count;
        }, 0);
        const totalEntradasIngresos = entries.reduce(
            (sum, e) => sum + (e.entradas?.ingresos_temporada || 0),
            0,
        );

        return {
            abonos: {
                ingresos_temporada: totalAbonosIngresos,
                numero_abonados_temporada: totalAbonados,
                precio_medio_temporada:
                    totalAbonados > 0 ? totalAbonosPrecioPonderado / totalAbonados : 0,
            },
            entradas: {
                vendidas_temporada: totalEntradasVendidas,
                precio_medio_temporada:
                    totalEntradasVendidas > 0
                        ? totalEntradasPrecioPonderado / totalEntradasVendidas
                        : 0,
                ingresos_temporada: totalEntradasIngresos,
            },
        };
    });

    /** KPIs formateados para los StatsCards */
    const kpis = computed(() => {
        if (isMultiSeason.value && aggregateKpis.value) {
            return aggregateKpis.value;
        }
        const entry = currentSeasonEntry.value;
        if (!entry) return null;

        return {
            abonos: {
                ingresos_temporada: entry.abonos?.ingresos_temporada ?? 0,
                numero_abonados_temporada: entry.abonos?.numero_abonados_temporada ?? 0,
                precio_medio_temporada: entry.abonos?.precio_medio_temporada ?? 0,
            },
            entradas: {
                vendidas_temporada: entry.entradas?.vendidas_temporada ?? 0,
                precio_medio_temporada: entry.entradas?.precio_medio_temporada ?? 0,
                ingresos_temporada: entry.entradas?.ingresos_temporada ?? 0,
            },
        };
    });

    /**
     * Datos normalizados para todos los gráficos.
     * En modo individual usa el tipo del CHART_CONFIG.
     * En modo multitemporada fuerza ColumnChart con una fila por temporada.
     *
     * @type {import('vue').ComputedRef<Map<string, { data: Array, type: string }>>}
     */
    const chartData = computed(() => {
        const result = new Map();

        if (!economicData.value) return result;

        const perSeason = economicData.value?.data?.per_season || {};
        const seasonEntries = Object.entries(perSeason).filter(
            ([, entry]) => entry,
        );

        for (const config of CHART_CONFIG) {
            const { field, type } = config;

            // === Synthetic field: renovados_nuevos_no_renovados ===
            // La API no tiene este campo directamente. Hay que fusionar
            // renovados, no_renovados y nuevos desde la raíz del grupo.
            if (field === "renovados_nuevos_no_renovados") {
                if (isMultiSeason.value) {
                    const rows = [["Temporada", "Renovados", "No renovados", "Nuevos"]];
                    for (const [seasonId, entry] of seasonEntries) {
                        const groupData = entry[config.group] || {};
                        rows.push([
                            getSeasonName(seasonsRef.value, seasonId),
                            extractChartValue(groupData.renovados),
                            extractChartValue(groupData.no_renovados),
                            extractChartValue(groupData.nuevos),
                        ]);
                    }
                    result.set(field, { data: rows, type: "ColumnChart" });
                } else {
                    const groupData = currentSeasonEntry.value?.[config.group] || {};
                    const data = [
                        ["Categoría", "Total"],
                        ["Renovados", extractChartValue(groupData.renovados)],
                        ["No renovados", extractChartValue(groupData.no_renovados)],
                        ["Nuevos", extractChartValue(groupData.nuevos)],
                    ];
                    result.set(field, { data, type });
                }
                continue;
            }

            // === Synthetic field: compradores_rango_edad ===
            // Suma la distribución de abonados + compradores de entradas por rango de edad.
            if (field === "compradores_rango_edad") {
                if (isMultiSeason.value) {
                    const allKeys = new Set();
                    for (const [, entry] of seasonEntries) {
                        Object.keys(entry?.abonos?.rango_edad || {}).forEach(k => allKeys.add(k));
                        Object.keys(entry?.entradas?.compradores_rango_edad || {}).forEach(k => allKeys.add(k));
                    }
                    if (allKeys.size === 0) {
                        result.set(field, { data: [], type: "ColumnChart" });
                        continue;
                    }
                    const sortedKeys = Array.from(allKeys);
                    const rows = [["Temporada", ...sortedKeys]];
                    for (const [seasonId, entry] of seasonEntries) {
                        const row = [getSeasonName(seasonsRef.value, seasonId)];
                        const abonoRanges = entry?.abonos?.rango_edad || {};
                        const entradaRanges = entry?.entradas?.compradores_rango_edad || {};
                        for (const key of sortedKeys) {
                            row.push((extractChartValue(abonoRanges[key]) || 0) + (extractChartValue(entradaRanges[key]) || 0));
                        }
                        rows.push(row);
                    }
                    result.set(field, { data: rows, type: "ColumnChart" });
                } else {
                    const abonoRanges = currentSeasonEntry.value?.abonos?.rango_edad || {};
                    const entradaRanges = currentSeasonEntry.value?.entradas?.compradores_rango_edad || {};
                    const allKeys = [...new Set([...Object.keys(abonoRanges), ...Object.keys(entradaRanges)])].sort();
                    const rows = [["Concepto", "Total"]];
                    for (const key of allKeys) {
                        rows.push([key, (extractChartValue(abonoRanges[key]) || 0) + (extractChartValue(entradaRanges[key]) || 0)]);
                    }
                    result.set(field, { data: rows, type });
                }
                continue;
            }

            // === Synthetic field: pedidos_por_tamano (con tooltips + %) ===
            if (field === "pedidos_por_tamano") {
                if (isMultiSeason.value) {
                    const allKeys = new Set();
                    for (const [, entry] of seasonEntries) {
                        const groupData = entry[config.group]?.[field];
                        if (groupData) {
                            Object.keys(groupData).forEach(k => allKeys.add(k));
                        }
                    }

                    if (allKeys.size === 0) {
                        result.set(field, { data: [], type: "ColumnChart" });
                        continue;
                    }

                    const sortedKeys = Array.from(allKeys);
                    // Header: ["Temporada", "1", {tooltip}, "2", {tooltip}, ...]
                    const header = ["Temporada"];
                    for (const key of sortedKeys) {
                        header.push(key);
                        header.push({ type: 'string', role: 'tooltip' });
                    }
                    const rows = [];
                    for (const [seasonId, entry] of seasonEntries) {
                        const row = [getSeasonName(seasonsRef.value, seasonId)];
                        // Calcular total del season para los %
                        let seasonTotal = 0;
                        const values = {};
                        for (const key of sortedKeys) {
                            const val = extractChartValue(entry[config.group]?.[field]?.[key]);
                            values[key] = val;
                            seasonTotal += val;
                        }
                        const fmt = n => new Intl.NumberFormat('es-ES').format(n);
                        for (const key of sortedKeys) {
                            const val = values[key];
                            row.push(val);
                            const pct = seasonTotal > 0 ? (val / seasonTotal) * 100 : 0;
                            row.push(`${fmt(val)} pedidos (${pct.toFixed(1)}%)`);
                        }
                        rows.push(row);
                    }
                    result.set(field, { data: [header, ...rows], type: "ColumnChart" });
                } else {
                    // Single: PieChart con tooltips formateados con {v, f}
                    const fieldData = currentSeasonEntry.value?.[config.group]?.[field];
                    const rawData = objectToChartData(fieldData);
                    if (rawData.length > 1) {
                        // Calcular total
                        let total = 0;
                        for (let i = 1; i < rawData.length; i++) {
                            total += (rawData[i][1] || 0);
                        }
                        const fmt = n => new Intl.NumberFormat('es-ES').format(n);
                        const rows = [["Concepto", "Total"]];
                        for (let i = 1; i < rawData.length; i++) {
                            const label = rawData[i][0];
                            const val = rawData[i][1] || 0;
                            const pct = total > 0 ? (val / total) * 100 : 0;
                            rows.push([{ v: label, f: `${label} — ${fmt(val)} ped. (${pct.toFixed(1)}%)` }, val]);
                        }
                        result.set(field, { data: rows, type });
                    } else {
                        result.set(field, { data: [], type });
                    }
                }
                continue;
            }

            if (isMultiSeason.value) {
                // Multitemporada: ColumnChart con columnas por temporada
                // Recolectar todas las claves disponibles entre todas las temporadas
                const allKeys = new Set();
                for (const [, entry] of seasonEntries) {
                    const groupData = entry[config.group]?.[field];
                    if (groupData) {
                        Object.keys(groupData).forEach(k => allKeys.add(k));
                    }
                }

                if (allKeys.size === 0) {
                    result.set(field, { data: [], type: "ColumnChart" });
                    continue;
                }

                const sortedKeys = Array.from(allKeys);
                const rows = [["Temporada", ...sortedKeys]];
                for (const [seasonId, entry] of seasonEntries) {
                    const row = [getSeasonName(seasonsRef.value, seasonId)];
                    for (const key of sortedKeys) {
                        const val = entry[config.group]?.[field]?.[key];
                        // Normalizar objetos anidados { total, porcentaje, importe }
                        row.push(extractChartValue(val));
                    }
                    rows.push(row);
                }
                result.set(field, { data: rows, type: "ColumnChart" });
            } else {
                // Individual: usar el tipo configurado
                const fieldData = currentSeasonEntry.value?.[config.group]?.[field];
                const data = objectToChartData(fieldData);
                result.set(field, { data, type });
            }
        }

        return result;
    });

    /** Configuración de gráficos filtrada y enriquecida con el tipo real */
    const chartConfigs = computed(() => {
        return CHART_CONFIG.map(cfg => ({
            ...cfg,
            resolvedType: isMultiSeason.value ? "ColumnChart" : cfg.type,
        }));
    });

    /**
     * Datos de geografía agregados en formato tabla.
     * Extrae las top N entradas de compradores_geografia de todas las temporadas,
     * sumando totales cuando una misma etiqueta aparece en varias temporadas.
     *
     * @type {import('vue').ComputedRef<{ provincias: Array, localidades: Array, codigos_postales: Array }>}
     */
    const geographyTableData = computed(() => {
        if (!economicData.value)
            return { provincias: [], localidades: [], codigos_postales: [] };
        const perSeason = economicData.value?.data?.per_season || {};
        const geography = {};
        for (const [, entry] of Object.entries(perSeason)) {
            const geo = entry?.entradas?.compradores_geografia;
            if (!geo) continue;
            for (const [dim, items] of Object.entries(geo)) {
                if (!Array.isArray(items)) continue;
                if (!geography[dim]) geography[dim] = [];
                for (const item of items) {
                    const existing = geography[dim].find(e => e.label === item.label);
                    if (existing) {
                        existing.total += item.total || 0;
                    } else {
                        geography[dim].push({ label: item.label, total: item.total || 0 });
                    }
                }
            }
        }
        // Sort by total desc, take top 10
        for (const dim of Object.keys(geography)) {
            geography[dim].sort((a, b) => b.total - a.total);
            geography[dim] = geography[dim].slice(0, 10);
        }
        return geography;
    });

    /**
     * Desglose por temporada de abonos y entradas.
     * Devuelve arrays planos con los datos de cada temporada más los totales agregados.
     *
     * @type {import('vue').ComputedRef<{ abonos: Array, entradas: Array, totals: object }|null>}
     */
    const seasonBreakdown = computed(() => {
        if (!economicData.value) return null;
        const perSeason = economicData.value?.data?.per_season || {};
        const abonos = [];
        const entradas = [];
        const totals = {
            abonos: { ingresos: 0, count: 0, ingresos_medios: 0 },
            entradas: {
                ingresos: 0,
                vendidas: 0,
                asistencia: 0,
                ingresos_medios: 0,
                asistencia_media: 0,
            },
        };

        for (const [seasonId, entry] of Object.entries(perSeason)) {
            const name = getSeasonName(seasonsRef.value, seasonId);

            if (entry?.abonos) {
                abonos.push({
                    season: name,
                    ingresos: entry.abonos.ingresos_temporada || 0,
                    count: entry.abonos.numero_abonados_temporada || 0,
                });
                totals.abonos.ingresos += entry.abonos.ingresos_temporada || 0;
                totals.abonos.count += entry.abonos.numero_abonados_temporada || 0;
            }
            if (entry?.entradas) {
                entradas.push({
                    season: name,
                    ingresos: entry.entradas.ingresos_temporada || 0,
                    vendidas: entry.entradas.vendidas_temporada || 0,
                    asistencia: entry.entradas.asistencia_media_temporada || 0,
                });
                totals.entradas.ingresos += entry.entradas.ingresos_temporada || 0;
                totals.entradas.vendidas += entry.entradas.vendidas_temporada || 0;
                totals.entradas.asistencia +=
                    entry.entradas.asistencia_media_temporada || 0;
            }
        }

        // Calculate averages
        if (abonos.length > 0) {
            totals.abonos.ingresos_medios = totals.abonos.ingresos / abonos.length;
        }
        if (entradas.length > 0) {
            totals.entradas.ingresos_medios =
                totals.entradas.ingresos / entradas.length;
            totals.entradas.asistencia_media =
                totals.entradas.asistencia / entradas.length;
        }

        return { abonos, entradas, totals };
    });

    // ==================== Chart Options ====================

    /**
     * Opciones por defecto para cada chart según su tipo.
     *
     * @param {string} field — nombre del campo del chart
     * @returns {object} — opciones para Google Charts
     */
    function getChartOptions(field) {
        const cfg = chartConfigs.value.find(c => c.field === field);
        const isColumn = (cfg?.resolvedType || "PieChart") === "ColumnChart";

        const base = {
            legend: { position: "bottom" },
            chartArea: { width: "80%", height: "70%" },
            colors: [
                "#3B82F6",
                "#10B981",
                "#F59E0B",
                "#EF4444",
                "#8B5CF6",
                "#EC4899",
            ],
        };

        if (isColumn) {
            return base;
        }

        return {
            ...base,
            pieHole: 0.3,
        };
    }

    return {
        // Estado
        economicData,
        loading,
        error,

        // Computed
        kpis,
        chartData,
        chartConfigs,
        isMultiSeason,
        geographyTableData,
        seasonBreakdown,

        // Métodos
        refresh,
        getChartOptions,
    };
}
